/**
 * Production-editor node renderers — port of the real Nemo nodes.
 *
 * Use:
 *   import { NODE_TYPES } from '@shared/production-editor/nodes';
 *   <ProductionEditorCanvas nodeTypes={NODE_TYPES} />
 *
 * Each node expects `data: IBaseData` (entityName + entityLabel + label),
 * mirroring `apps/frontend-next/src/ui/data/ProductionEditor.tsx`.
 */
import { useMemo } from 'react';
import {
    Handle,
    NodeResizeControl,
    Position,
    type Node,
    type NodeProps,
} from '@xyflow/react';
import {
    NJIcon,
    NJTooltip,
} from '@engie-group/fluid-design-system-react';
import CustomNode from './CustomNode';

export interface IBaseData extends Record<string, unknown> {
    entityName: string;
    entityLabel: string;
    label: string;
    onEditClick?: () => void;
    onDeleteClick?: () => void;
    onDuplicateClick?: () => void;
    chillerType?: 'Centrifuge' | 'Multi-centrifuge' | 'Scroll';
}

export type AssetNode = Node<IBaseData>;
export type ContractNode = Node<IBaseData>;
export type GroupNode = Node<IBaseData>;
export type GroupMemberNode = Node<IBaseData>;

// --- Asset --------------------------------------------------------------
export function Asset(props: NodeProps<AssetNode>) {
    const {
        id,
        selected,
        data: { entityName, entityLabel, label, onEditClick, onDeleteClick, onDuplicateClick, chillerType },
    } = props;

    const handles = useMemo(() => {
        if (entityName === 'Return') return [Position.Right];
        if (entityName === 'Departure') return [Position.Left];
        if (entityName === 'StorageTank' || entityName === 'storage_tank') return [Position.Top, Position.Left, Position.Right];
        if (entityName === 'geothermal_unit') return [Position.Left, Position.Right, Position.Bottom];
        if (entityName === 'heat_pump') return [Position.Top, Position.Left, Position.Right];
        // Boilers receive fuel contracts from the top and connect to fluid flow left/right
        if (entityName === 'Boiler' || entityName === 'boiler' || entityName === 'SteamProductionUnit')
            return [Position.Top, Position.Left, Position.Right];
        return [Position.Left, Position.Right];
    }, [entityName]);

    return (
        <CustomNode
            id={id}
            selected={selected}
            entityName={entityName}
            entityLabel={entityLabel}
            label={label}
            handles={handles}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onDuplicateClick={onDuplicateClick}
            chillerType={chillerType}
        />
    );
}

// --- Contract -----------------------------------------------------------
export function Contract(props: NodeProps<ContractNode>) {
    const {
        id,
        selected,
        data: { entityName, entityLabel, label, onEditClick, onDeleteClick, onDuplicateClick },
    } = props;
    return (
        <CustomNode
            id={id}
            selected={selected}
            entityName={entityName}
            entityLabel={entityLabel}
            label={label}
            // FuelContract: source from bottom; PowerContract: source from top
            handles={[
                entityName === 'PowerContract' ? Position.Top : Position.Bottom,
                Position.Left,
                Position.Right,
            ]}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onDuplicateClick={onDuplicateClick}
        />
    );
}

// --- Group --------------------------------------------------------------
// Left/Right for fluid flow; Top/Bottom for vertical connections
const GROUP_HANDLES = [Position.Left, Position.Right, Position.Top, Position.Bottom];

export function Group(props: NodeProps<GroupNode>) {
    const {
        selected,
        data: { entityLabel, label, onEditClick, onDeleteClick, onDuplicateClick },
    } = props;
    return (
        <div
            style={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                textAlign: 'center',
                flex: 1,
                fontFamily: 'var(--nj-core-font-family-sans)',
                padding: 12,
                minWidth: 0,
                outline: selected
                    ? '2px solid var(--nj-semantic-color-border-brand-strong-default)'
                    : 'none',
                outlineOffset: 1,
            }}
        >
            {/* Barre d'actions flottante au hover/select */}
            {selected && (onEditClick || onDeleteClick || onDuplicateClick) && (
                <div
                    style={{
                        position: 'absolute',
                        top: -36,
                        left: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: 4,
                        background: 'white',
                        border: '1px solid var(--nj-core-color-grey-300)',
                        borderRadius: '6px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        zIndex: 10,
                        width: 'max-content',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {onEditClick && (
                        <NJTooltip text="Modifier" isColorInverse={false} hasArrow placement="top">
                            <button
                                type="button"
                                onClick={onEditClick}
                                aria-label="Modifier"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: 0,
                                    background: 'transparent',
                                }}
                            >
                                <NJIcon name="edit" />
                            </button>
                        </NJTooltip>
                    )}
                    {onDuplicateClick && (
                        <NJTooltip text="Dupliquer" isColorInverse={false} hasArrow placement="top">
                            <button
                                type="button"
                                onClick={onDuplicateClick}
                                aria-label="Dupliquer"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: 0,
                                    background: 'transparent',
                                }}
                            >
                                <NJIcon name="content_copy" />
                            </button>
                        </NJTooltip>
                    )}
                    {onDeleteClick && (
                        <NJTooltip text="Supprimer" isColorInverse={false} hasArrow placement="top">
                            <button
                                type="button"
                                onClick={onDeleteClick}
                                aria-label="Supprimer"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: 0,
                                    background: 'transparent',
                                }}
                            >
                                <NJIcon name="delete" />
                            </button>
                        </NJTooltip>
                    )}
                </div>
            )}
            
            <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
            <span style={{ fontSize: 11, color: 'var(--nj-semantic-color-text-neutral-tertiary-default)', lineHeight: 1.3 }}>
                {entityLabel}
            </span>
            {GROUP_HANDLES.map((h) => (
                <Handle key={`s-${h}`} type="source" position={h} id={`s-${h}`} />
            ))}
            {GROUP_HANDLES.map((h) => (
                <Handle key={`t-${h}`} type="target" position={h} id={h} />
            ))}
            <NodeResizeControl />
        </div>
    );
}

// --- GroupMember --------------------------------------------------------
export function GroupMember(props: NodeProps<GroupMemberNode>) {
    const {
        id,
        selected,
        data: { entityName, entityLabel, label, onEditClick, onDeleteClick, onDuplicateClick },
    } = props;
    return (
        <CustomNode
            id={id}
            selected={selected}
            entityName={entityName}
            entityLabel={entityLabel}
            label={label}
            handles={[]}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onDuplicateClick={onDuplicateClick}
        />
    );
}

export const NODE_TYPES = {
    asset: Asset,
    contract: Contract,
    group: Group,
    groupMember: GroupMember,
};
